import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { track, trackConversion, identify } from "@/lib/analytics";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    identify(email);
    return res.data;
  }, []);

  const registerBusiness = useCallback(async (data) => {
    const res = await api.post("/auth/register/business", data);
    setUser(res.data);
    identify(data?.email);
    let source = ""; try { source = localStorage.getItem("sc_signup_source") || ""; } catch {}
    trackConversion("biz_signup", { method: "email", source });
    track("sign_up", { method: "email", role: "business", source });
    try { localStorage.setItem("sc_welcome", "business"); } catch {}
    return res.data;
  }, []);

  const registerSalesman = useCallback(async (data) => {
    const res = await api.post("/auth/register/salesman", data);
    setUser(res.data);
    identify(data?.email);
    let source = ""; try { source = localStorage.getItem("sc_signup_source") || ""; } catch {}
    trackConversion("rep_signup", { method: "email", source });
    track("sign_up", { method: "email", role: "salesman", source });
    try { localStorage.setItem("sc_welcome", "salesman"); } catch {}
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, refresh, login, registerBusiness, registerSalesman, logout }),
    [user, loading, refresh, login, registerBusiness, registerSalesman, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
